import {ApplicationConfig, ErrorHandler, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, withComponentInputBinding} from '@angular/router';
import {routes} from 'src/app/app.routes';
import {provideHttpClient, withInterceptors, withJsonpSupport} from '@angular/common/http';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {BrowserModule} from "@angular/platform-browser";
import {ToWaterPointIconPipe} from "src/app/presentation/common/pipes/ToWaterPointIconPipe";
import {ToGatewayIconPipe} from "src/app/presentation/common/pipes/ToGatewayIconPipe";
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideNativeDateAdapter} from "@angular/material/core";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {Animal, Asset, LoRaGatewayStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {authenticationInterceptor, errorResponseInterceptor} from "src/app/infrastructure/http-interceptors";
import {fiveMinutes, oneHour, sevenDays, twentyFourHours} from "src/app/presentation/common/globals";
import {Notification} from "src/app/infrastructure/moovement-api/models/notification";
import * as Sentry from "@sentry/angular";

export function distinct(value: any, index: any, array: any[]): boolean {
  return array.indexOf(value) === index;
}

export function sinceFiveMinutes() {
  const since = new Date()
  since.setTime(new Date().getTime() - fiveMinutes)
  return since
}

export function sinceOneHour() {
  const since = new Date()
  since.setTime(new Date().getTime() - oneHour)
  return since
}

export function sinceOneDay() {
  const since = new Date()
  since.setTime(new Date().getTime() - twentyFourHours)
  return since
}

export function sinceOneWeek() {
  const since = new Date()
  since.setTime(new Date().getTime() - sevenDays)
  return since
}

export function filterAssetSamples<A extends Asset>(
  originalMap: Map<A, Array<Samples.AssetSample<any>>>,
  filterFn: (item: Samples.AssetSample<any>) => boolean
): Map<A, Array<Samples.AssetSample<any>>> {
  const newMap = new Map<A, Array<Samples.AssetSample<any>>>();

  originalMap.forEach((array, animal) => {
    const filteredArray = array.filter(filterFn);
    newMap.set(animal, filteredArray);
  });

  return newMap;
}

export function filterAssetsWhereEveryAssetSample<A extends Asset>(
  originalMap: Map<A, Array<Samples.AssetSample<any>>>,
  filterFn: (item: Samples.AssetSample<any>) => boolean
): Map<A, Array<Samples.AssetSample<any>>> {
  const newMap = new Map<A, Array<Samples.AssetSample<any>>>();
  originalMap.forEach((array, animal) => {
    if (array.every(filterFn)) {
      newMap.set(animal, array);
    }
  });
  return newMap;
}

export function filterAssetSamplesWhereAssetSample<A extends Asset>(
  originalMap: Map<A, Array<Samples.AssetSample<any>>>,
  filterFn: (item: Samples.AssetSample<any>) => boolean
): Map<A, Array<Samples.AssetSample<any>>> {
  const newMap = new Map<A, Array<Samples.AssetSample<any>>>();
  originalMap.forEach((array, animal) => {
    newMap.set(animal, array.filter(filterFn));
  });
  return newMap;
}

export function filterAssetsWhereLastAssetSample<A extends Asset>(
  originalMap: Map<A, Array<Samples.AssetSample<any>>>,
  filterFn: (item: Samples.AssetSample<any>) => boolean
): Map<Animal, Samples.AssetSample<any>[]> {
  const newMap = new Map<Animal, Samples.AssetSample<any>[]>();
  originalMap.forEach((array, animal) => {
    if (filterFn(array[0])) {
      newMap.set(animal, array);
    }
  });
  return newMap;
}

export function nowMinusHours(hours: number): Date {
  const now = new Date()
  now.setHours(now.getHours() - hours)
  return now
}

export function nonEmptyResult(): (entry: [Animal, Array<Samples.AssetSample<any>>]) => boolean {
  return entry => entry[0] != undefined && entry[1].length != 0
}

export function descendingLoRaGatewayStatuses() {
  return (a: LoRaGatewayStatus, b: LoRaGatewayStatus) => {
    if (!a?.instant || !b?.instant) {
      return 0;
    }
    const timeA = new Date(a.instant!).getTime()
    const timeB = new Date(b.instant!).getTime()
    return compare(timeA, timeB)
  }
}

export function descendingAssetSampleEntries<A extends Asset>() {
  return (a: [A, Samples.AssetSample<any>[]], b: [A, Samples.AssetSample<any>[]]) => {
    const a1 = a[1].sort(descendingAssetSamples())[0]
    const b1 = b[1].sort(descendingAssetSamples())[0]
    const timeA = new Date(a1.sample!.instant!).getTime()
    const timeB = new Date(b1.sample!.instant!).getTime()
    return compare(timeA, timeB)
  }
}

export function ascendingAssetSampleEntries<A extends Asset>() {
  return (a: [A, Samples.AssetSample<any>[]], b: [A, Samples.AssetSample<any>[]]) => {
    const a1 = a[1].sort(ascendingAssetSamples())[0]
    const b1 = b[1].sort(ascendingAssetSamples())[0]
    const timeA = new Date(a1.sample!.instant!).getTime()
    const timeB = new Date(b1.sample!.instant!).getTime()
    return compare(timeB, timeA)
  }
}

export function hasMatchingKey<K, V>(map: Map<K, V>, filter: (k: K) => boolean): boolean {
  if (map.size == 0) {
    return false
  }
  const key = Array.from(map.keys()).filter(it => it != undefined).find(filter)
  return key != undefined
}

export function getKeyByKeyMatching<K, V>(map: Map<K, V>, filter: (k: K) => boolean): K | undefined {
  if (map.size == 0) {
    return undefined
  }
  const key = Array.from(map.keys()).filter(it => it != undefined).find(filter)
  if (key)
    return key
  return undefined
}

export function getValueByKeyMatching<K, V>(map: Map<K, V>, filter: (k: K) => boolean): V | undefined {
  if (map.size == 0) {
    return undefined
  }
  const key = Array.from(map.keys()).filter(it => it != undefined).find(filter)
  if (key)
    return map.get(key)
  return undefined
}

export function isEqualToOrOlderThanOffset(date: Date, offset: number): boolean {
  return date.getTime() <= Date.now() - offset;
}

export function isEqualToOrYoungerThanOffset(date: Date, offset: number): boolean {
  return date.getTime() >= Date.now() - offset;
}

export function isPresentOrFuture(date: Date) {
  const present = new Date();
  return date >= present;
}

export function compare(a: number, b: number) {
  if (a > b) {
    return 1
  }
  if (a < b) {
    return -1
  }
  return 0
}

export function ascendingAssetSamples() {
  return (a: Samples.AssetSample<any>, b: Samples.AssetSample<any>) => {
    const timeA = new Date(a.sample!.instant!).getTime()
    const timeB = new Date(b.sample!.instant!).getTime()
    return compare(timeB, timeA)
  }
}

export function descendingAssetSamples() {
  return (a: Samples.AssetSample<any>, b: Samples.AssetSample<any>) => {
    const timeA = new Date(a.sample!.instant!).getTime()
    const timeB = new Date(b.sample!.instant!).getTime()
    return compare(timeA, timeB)
  }
}

export function descendingDates() {
  return (a: Date, b: Date) => {
    return compare(a.getTime(), b.getTime())
  }
}

export function descendingNotifications() {
  return (a: Notification, b: Notification) => {
    const timeA = new Date(a.issueTime!).getTime()
    const timeB = new Date(b.issueTime!).getTime()
    return compare(timeB, timeA)
  }
}

export function last(...dates: (Date | undefined)[]): Date | undefined {
  return dates
  .filter((d): d is Date => d != null)
  .sort((a, b) => compare(a.getTime(), b.getTime()))[0];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withJsonpSupport(), withInterceptors([
      authenticationInterceptor,
      errorResponseInterceptor,
    ])),
    provideNativeDateAdapter(),
    provideZoneChangeDetection({eventCoalescing: true}),
    ToWaterPointIconPipe,
    ToGatewayIconPipe,
    importProvidersFrom([BrowserAnimationsModule, BrowserModule]),
    provideRouter(routes, withComponentInputBinding()), provideAnimationsAsync(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    }
  ]
};
